/**
 * Centralized Furniture Constraints Configuration
 *
 * This file defines all dimension ranges, section rules, pricing formulas,
 * and validation logic for furniture products.
 *
 * Benefits:
 * - Single source of truth for all constraints
 * - Type-safe with TypeScript
 * - Easy to modify and test
 * - No magic numbers scattered in code
 */

import { getFullPalette } from '~/components/ProductPage/productTypes/wardrobeConfigFactory'
import {
  ColumnConfigurationType,
  getDrawerCount,
} from '~/types/columnConfigurationTypes'

interface DimensionConstraints {
  min: number
  max: number
  default: number
  unit?: string
}

interface DimensionRanges {
  width: DimensionConstraints
  height: DimensionConstraints
  depth: DimensionConstraints
  plintHeight: DimensionConstraints
}

interface DimensionSteps {
  widthStep?: number
  heightStep?: number
  depthStep?: number
}

interface SectionConstraints {
  rule: 'height-based' | 'width-based' | 'auto' | 'fixed'
  min: number
  max: number
  default: number
  getAvailableSections?: (dimensions: {
    width: number
    height: number
  }) => number[]
  autoCalculate?: (dimensions: { width: number; height: number }) => number
}

interface ColumnConstraints {
  min: number
  max: number
  default: number
  allowCustomConfiguration: boolean
  getAvailableColumns?: (dimensions: {
    width: number
    height: number
  }) => number[]
}

type DoorType = 'hinged' | 'sliding'

/**
 * New unified pricing model (NO legacy).
 *
 * Total = material(m² carcass/back/facades) + hardware(per drawer/door + base) + extras + VAT
 *
 * Notes:
 * - "facadePerM2" is used for visible fronts (doors, drawer fronts, etc.)
 * - "hardwarePerDrawer" is for slides + assembly labor per drawer (not material m²)
 * - "hardwarePerDoor" is for hinges/handles or per-door cost (not material m²)
 * - "slidingSystemExtra" can be applied once for sliding wardrobes (tracks, etc.)
 */
interface M2PricingFormula {
  kind: 'm2'

  carcassPerM2: number
  facadePerM2: number
  backPanelPerM2?: number

  wasteFactor: number

  hardwareBase: number
  hardwarePerDrawer?: number
  hardwarePerDoor?: number
  pushToOpenExtraPerDrawer?: number

  // wardrobe-specific knobs (safe to set for others too; they just won't be used)
  defaultDoorType?: DoorType
  defaultSlidingOverlapCm?: number
  slidingSystemExtra?: number

  vatMultiplier: number
}

type PricingFormula = M2PricingFormula

interface ImageConstraints {
  heightThresholds: Array<{ maxHeight: number; imageDimension: number }>
  widthThresholds: Array<{ maxWidth: number; imageDimension: number }>
  plintHeightThresholds: Array<{
    maxPlintHeight: number
    imageDimension: number
  }>
}

export interface ProductConstraints {
  dimensions: DimensionRanges
  steps?: DimensionSteps
  sections: SectionConstraints
  columns: ColumnConstraints
  pricing: PricingFormula
  images: ImageConstraints
  colors: string[]
}

// ============================================================================
// STAND CONSTRAINTS
// ============================================================================

const STAND_CONSTRAINTS: ProductConstraints = {
  dimensions: {
    width: { min: 50, max: 240, default: 80, unit: 'cm' },
    height: { min: 70, max: 130, default: 70, unit: 'cm' },
    depth: { min: 35, max: 50, default: 40, unit: 'cm' },
    plintHeight: { min: 2, max: 10, default: 2, unit: 'cm' },
  },

  steps: {
    heightStep: 5,
  },

  sections: {
    rule: 'height-based',
    min: 3,
    max: 5,
    default: 4,
    getAvailableSections: ({ height }) => {
      if (height >= 130) return [5]
      if (height >= 105) return [4, 5]
      return [3, 4]
    },
  },

  columns: {
    min: 1,
    max: 10,
    default: 1,
    allowCustomConfiguration: true,
    getAvailableColumns: ({ height }) => {
      if (height >= 50 && height <= 84) return [1]
      if (height >= 85 && height <= 104) return [1, 2]
      if (height >= 105 && height <= 149) return [2]
      if (height >= 150 && height <= 205) return [2, 3]
      if (height >= 206 && height <= 270) return [3]
      return [1]
    },
  },

  pricing: {
    kind: 'm2',

    // placeholders; calibrate with your BOM/supplier costs
    carcassPerM2: 850,
    facadePerM2: 1050,
    backPanelPerM2: 650,

    wasteFactor: 1.12,

    hardwareBase: 250,
    hardwarePerDrawer: 650,
    pushToOpenExtraPerDrawer: 250,

    vatMultiplier: 1.3,
  },

  images: {
    heightThresholds: [
      { maxHeight: 90, imageDimension: 700 },
      { maxHeight: 110, imageDimension: 900 },
      { maxHeight: Infinity, imageDimension: 1200 },
    ],
    widthThresholds: [
      { maxWidth: 80, imageDimension: 600 },
      { maxWidth: 100, imageDimension: 800 },
      { maxWidth: Infinity, imageDimension: 1000 },
    ],
    plintHeightThresholds: [
      { maxPlintHeight: 5, imageDimension: 20 },
      { maxPlintHeight: Infinity, imageDimension: 60 },
    ],
  },

  colors: getFullPalette(),
}

// ============================================================================
// BEDSIDE CONSTRAINTS
// ============================================================================

const BEDSIDE_CONSTRAINTS: ProductConstraints = {
  dimensions: {
    width: { min: 40, max: 80, default: 60, unit: 'cm' },
    height: { min: 30, max: 60, default: 40, unit: 'cm' },
    depth: { min: 35, max: 50, default: 40, unit: 'cm' },
    plintHeight: { min: 2, max: 10, default: 2, unit: 'cm' },
  },

  steps: {
    heightStep: 1,
  },

  sections: {
    rule: 'auto',
    min: 1,
    max: 2,
    default: 1,
    autoCalculate: ({ height }) => (height > 35 ? 2 : 1),
  },

  columns: {
    min: 1,
    max: 10,
    default: 1,
    allowCustomConfiguration: true,
  },

  pricing: {
    kind: 'm2',

    carcassPerM2: 850,
    facadePerM2: 1050,
    backPanelPerM2: 650,

    wasteFactor: 1.12,

    hardwareBase: 200,
    hardwarePerDrawer: 650,
    pushToOpenExtraPerDrawer: 250,

    vatMultiplier: 1.3,
  },

  images: {
    heightThresholds: [
      { maxHeight: 36, imageDimension: 300 },
      { maxHeight: Infinity, imageDimension: 400 },
    ],
    widthThresholds: [
      { maxWidth: 50, imageDimension: 500 },
      { maxWidth: 70, imageDimension: 600 },
      { maxWidth: Infinity, imageDimension: 800 },
    ],
    plintHeightThresholds: [
      { maxPlintHeight: 5, imageDimension: 20 },
      { maxPlintHeight: Infinity, imageDimension: 60 },
    ],
  },

  colors: getFullPalette(),
}

// ============================================================================
// TV STAND CONSTRAINTS
// ============================================================================

const TV_STAND_CONSTRAINTS: ProductConstraints = {
  dimensions: {
    width: { min: 80, max: 270, default: 160, unit: 'cm' },
    height: { min: 30, max: 60, default: 45, unit: 'cm' },
    depth: { min: 35, max: 50, default: 40, unit: 'cm' },
    plintHeight: { min: 2, max: 10, default: 2, unit: 'cm' },
  },

  steps: {
    heightStep: 1,
  },

  sections: {
    rule: 'width-based',
    min: 1,
    max: 4,
    default: 2,
    getAvailableSections: ({ width }) => {
      if (width < 120) return [1, 2]
      if (width < 150) return [1, 2, 3]
      if (width < 190) return [2]
      return [2, 4]
    },
  },

  columns: {
    min: 1,
    max: 4,
    default: 2,
    allowCustomConfiguration: true,
  },

  pricing: {
    kind: 'm2',

    carcassPerM2: 850,
    facadePerM2: 1050,
    backPanelPerM2: 650,

    wasteFactor: 1.12,

    hardwareBase: 250,
    hardwarePerDrawer: 650,
    pushToOpenExtraPerDrawer: 250,

    vatMultiplier: 1.3,
  },

  images: {
    heightThresholds: [
      { maxHeight: 45, imageDimension: 300 },
      { maxHeight: Infinity, imageDimension: 400 },
    ],
    widthThresholds: [
      { maxWidth: 100, imageDimension: 800 },
      { maxWidth: 120, imageDimension: 1000 },
      { maxWidth: 150, imageDimension: 1200 },
      { maxWidth: 190, imageDimension: 1600 },
      { maxWidth: Infinity, imageDimension: 2000 },
    ],
    plintHeightThresholds: [
      { maxPlintHeight: 5, imageDimension: 20 },
      { maxPlintHeight: Infinity, imageDimension: 60 },
    ],
  },

  colors: getFullPalette(),
}

// ============================================================================
// WARDROBE CONSTRAINTS
// ============================================================================

const WARDROBE_CONSTRAINTS: ProductConstraints = {
  dimensions: {
    width: { min: 40, max: 270, default: 150, unit: 'cm' },
    height: { min: 200, max: 280, default: 240, unit: 'cm' },
    depth: { min: 35, max: 60, default: 50, unit: 'cm' },
    plintHeight: { min: 2, max: 8, default: 2, unit: 'cm' },
  },

  steps: {
    heightStep: 1,
  },

  sections: {
    rule: 'width-based',
    min: 1,
    max: 5,
    default: 1,
    getAvailableSections: ({ width }) => {
      if (width <= 60) return [1]
      if (width <= 100) return [1, 2]
      if (width <= 150) return [2, 3]
      if (width <= 200) return [2, 3]
      return [2, 3]
    },
  },

  columns: {
    min: 1,
    max: 10,
    default: 3,
    allowCustomConfiguration: true,
    getAvailableColumns: ({ width }) => {
      if (width <= 60) return [1]
      if (width <= 100) return [2]
      if (width <= 150) return [2, 3]
      if (width < 200) return [3, 4]
      if (width === 200) return [4, 5]
      return [4, 5]
    },
  },

  pricing: {
    kind: 'm2',

    carcassPerM2: 750,
    facadePerM2: 750,
    backPanelPerM2: 750,

    wasteFactor: 1.12,

    hardwareBase: 350,
    hardwarePerDoor: 450,
    slidingSystemExtra: 1200,

    defaultDoorType: 'hinged',
    defaultSlidingOverlapCm: 4,

    vatMultiplier: 1.35,
  },

  images: {
    heightThresholds: [
      { maxHeight: 210, imageDimension: 2100 },
      { maxHeight: Infinity, imageDimension: 2400 },
    ],
    widthThresholds: [
      { maxWidth: 60, imageDimension: 50 },
      { maxWidth: 100, imageDimension: 80 },
      { maxWidth: 150, imageDimension: 120 },
      { maxWidth: 200, imageDimension: 160 },
      { maxWidth: Infinity, imageDimension: 200 },
    ],
    plintHeightThresholds: [
      { maxPlintHeight: 5, imageDimension: 20 },
      { maxPlintHeight: Infinity, imageDimension: 60 },
    ],
  },

  colors: ['White', 'Beige', 'Light Grey', 'Grey'],
}

// ============================================================================
// WARDROBE NEW COLLECTION CONSTRAINTS
// ============================================================================
const WARDROBE_NEW_COLLECTION_CONSTRAINTS: ProductConstraints = {
  dimensions: {
    width: { min: 40, max: 280, default: 150, unit: 'cm' },
    height: { min: 200, max: 280, default: 240, unit: 'cm' },
    depth: { min: 35, max: 60, default: 50, unit: 'cm' },
    plintHeight: { min: 2, max: 8, default: 2, unit: 'cm' },
  },

  steps: {
    heightStep: 1,
  },

  sections: {
    rule: 'width-based',
    min: 1,
    max: 5,
    default: 1,
    getAvailableSections: ({ width }) => {
      if (width <= 60) return [1]
      if (width <= 100) return [1, 2]
      if (width <= 150) return [2, 3]
      if (width <= 200) return [2, 3]
      return [2, 3]
    },
  },

  columns: {
    min: 1,
    max: 10,
    default: 3,
    allowCustomConfiguration: true,
    getAvailableColumns: ({ width }) => {
      if (width <= 60) return [1]
      if (width <= 100) return [2]
      if (width <= 150) return [2, 3]
      if (width < 200) return [3, 4]
      if (width === 200) return [4, 5]
      return [4, 5]
    },
  },

  pricing: {
    kind: 'm2',

    carcassPerM2: 850,
    facadePerM2: 1050,
    backPanelPerM2: 650,

    wasteFactor: 1.12,

    hardwareBase: 350,
    hardwarePerDoor: 450,
    slidingSystemExtra: 1200,

    defaultDoorType: 'hinged',
    defaultSlidingOverlapCm: 4,

    vatMultiplier: 1.35,
  },

  images: {
    heightThresholds: [
      { maxHeight: 210, imageDimension: 2100 },
      { maxHeight: Infinity, imageDimension: 2400 },
    ],
    widthThresholds: [
      { maxWidth: 60, imageDimension: 50 },
      { maxWidth: 100, imageDimension: 80 },
      { maxWidth: 150, imageDimension: 120 },
      { maxWidth: 200, imageDimension: 160 },
      { maxWidth: Infinity, imageDimension: 200 },
    ],
    plintHeightThresholds: [
      { maxPlintHeight: 5, imageDimension: 20 },
      { maxPlintHeight: Infinity, imageDimension: 60 },
    ],
  },

  colors: ['White', 'Beige', 'Light Grey', 'Grey'],
}

// ============================================================================
// RACK CONSTRAINTS
// ============================================================================

const RACK_CONSTRAINTS: ProductConstraints = {
  dimensions: {
    width: { min: 40, max: 350, default: 120, unit: 'cm' },
    height: { min: 90, max: 270, default: 200, unit: 'cm' },
    depth: { min: 25, max: 65, default: 35, unit: 'cm' },
    plintHeight: { min: 2, max: 8, default: 2, unit: 'cm' },
  },

  steps: {
    heightStep: 1,
    depthStep: 5,
  },

  sections: {
    rule: 'width-based',
    min: 1,
    max: 3,
    default: 1,
    getAvailableSections: ({ width }) => {
      if (width <= 80) return [1]
      if (width <= 160) return [1, 2]
      return [1, 2, 3]
    },
  },

  columns: {
    min: 1,
    max: 3,
    default: 2,
    allowCustomConfiguration: true,
    getAvailableColumns: ({ width }) => {
      if (width <= 80) return [1]
      if (width <= 160) return [2]
      return [3]
    },
  },

  pricing: {
    kind: 'm2',

    carcassPerM2: 820,
    facadePerM2: 0, // rack has no facades by default
    backPanelPerM2: 650,

    wasteFactor: 1.12,

    hardwareBase: 150,

    vatMultiplier: 1.35,
  },

  images: {
    heightThresholds: [
      { maxHeight: 150, imageDimension: 1500 },
      { maxHeight: 220, imageDimension: 2000 },
      { maxHeight: Infinity, imageDimension: 2400 },
    ],
    widthThresholds: [
      { maxWidth: 80, imageDimension: 80 },
      { maxWidth: 160, imageDimension: 120 },
      { maxWidth: Infinity, imageDimension: 180 },
    ],
    plintHeightThresholds: [
      { maxPlintHeight: 5, imageDimension: 20 },
      { maxPlintHeight: Infinity, imageDimension: 60 },
    ],
  },

  colors: getFullPalette(),
}

// ============================================================================
// BOM / m² HELPERS
// ============================================================================

interface M2BreakdownItem {
  name: string
  wCm: number
  hCm: number
  count: number
  areaM2: number // raw (without waste factor)
}

const cm2ToM2 = (cm2: number): number => cm2 / 10_000

const areaM2 = (wCm: number, hCm: number, count: number): number =>
  cm2ToM2(wCm * hCm) * count

const pushItem = (
  arr: M2BreakdownItem[],
  name: string,
  wCm: number,
  hCm: number,
  count: number
): void => {
  arr.push({ name, wCm, hCm, count, areaM2: areaM2(wCm, hCm, count) })
}

interface CarcassM2Params {
  widthCm: number
  heightCm: number
  depthCm: number
  columns: number

  plinthHeightCm: number
  plinthIsSeparatePiece: boolean

  hasBackPanel: boolean
}

function calculateCarcassM2(params: CarcassM2Params): M2BreakdownItem[] {
  const safeColumns = Math.max(1, Math.floor(params.columns))
  const plinthH = Math.max(0, params.plinthHeightCm)
  const sideHeight = params.plinthIsSeparatePiece
    ? Math.max(0, params.heightCm - plinthH)
    : params.heightCm

  const items: M2BreakdownItem[] = []

  // carcass outer
  pushItem(items, 'Side panels (L/R)', params.depthCm, sideHeight, 2)
  pushItem(items, 'Top + Bottom', params.widthCm, params.depthCm, 2)

  // partitions between columns
  const partitions = Math.max(0, safeColumns - 1)
  if (partitions > 0) {
    pushItem(
      items,
      'Vertical partitions',
      params.depthCm,
      sideHeight,
      partitions
    )
  }

  // back panel (PAL)
  if (params.hasBackPanel) {
    // keep full height (stable + simple). If you prefer excluding plinth area, replace with sideHeight.
    pushItem(items, 'Back panel (PAL)', params.widthCm, params.heightCm, 1)
  }

  return items
}

interface FacadeM2Params {
  type: FurnitureType

  widthCm: number
  heightCm: number
  plinthHeightCm: number
  plinthIsSeparatePiece: boolean

  doors?: number
  doorType?: DoorType
  slidingOverlapCm?: number
}

function calculateFacadeM2(params: FacadeM2Params): M2BreakdownItem[] {
  const items: M2BreakdownItem[] = []
  const plinthH = Math.max(0, params.plinthHeightCm)
  const facadeHeight = params.plinthIsSeparatePiece
    ? Math.max(0, params.heightCm - plinthH)
    : params.heightCm

  // drawers-based products: facade is typically full front (excluding plinth)
  if (
    params.type === 'stand' ||
    params.type === 'bedside' ||
    params.type === 'tv-stand'
  ) {
    pushItem(items, 'Drawer fronts total area', params.widthCm, facadeHeight, 1)
    return items
  }

  // rack: no facades by default
  if (params.type === 'rack') {
    return items
  }

  // wardrobe: doors
  if (params.type === 'wardrobe') {
    const safeDoors = Math.max(1, Math.floor(params.doors ?? 1))
    const doorType: DoorType = params.doorType ?? 'hinged'
    const overlap = Math.max(0, params.slidingOverlapCm ?? 4)

    if (doorType === 'hinged') {
      pushItem(
        items,
        'Doors (hinged) total area',
        params.widthCm,
        facadeHeight,
        1
      )
      return items
    }

    const overlapTotal = Math.max(0, safeDoors - 1) * overlap
    pushItem(
      items,
      'Doors (sliding) total area',
      params.widthCm + overlapTotal,
      facadeHeight,
      1
    )
    return items
  }

  return items
}

function calculateMaterialCost(
  carcassItems: M2BreakdownItem[],
  facadeItems: M2BreakdownItem[],
  pricing: M2PricingFormula
): number {
  const waste = pricing.wasteFactor

  const carcassCost = carcassItems.reduce((sum, item) => {
    const rate =
      item.name === 'Back panel (PAL)' && pricing.backPanelPerM2
        ? pricing.backPanelPerM2
        : pricing.carcassPerM2
    return sum + item.areaM2 * waste * rate
  }, 0)

  const facadeCost = facadeItems.reduce((sum, item) => {
    return sum + item.areaM2 * waste * pricing.facadePerM2
  }, 0)

  return carcassCost + facadeCost
}

const round10 = (n: number): number => Math.round(n / 10) * 10

const PRICE_DISCOUNT = 0.935

// ============================================================================
// WARDROBE PRICE CALCULATION (m²)
// ============================================================================

export interface WardrobePriceParams {
  width: number
  height: number
  depth: number

  columns: number
  doors: number

  doorType?: DoorType
  slidingOverlapCm?: number

  hasBackPanel?: boolean

  plintHeight?: number
  plinthIsSeparatePiece?: boolean

  // interior / templates extras
  sectionsPrice: number
  templatesExtraCost: number
}

export function calculateWardrobePrice(params: WardrobePriceParams): number {
  const { pricing, dimensions } = getConstraints('wardrobe')

  const safeDoors = Math.max(1, Math.floor(params.doors))
  const safeColumns = Math.max(1, Math.floor(params.columns))

  const plintHeight = params.plintHeight ?? dimensions.plintHeight.default
  const plinthIsSeparatePiece = params.plinthIsSeparatePiece ?? true

  const doorType: DoorType =
    params.doorType ?? pricing.defaultDoorType ?? 'hinged'
  const slidingOverlapCm =
    params.slidingOverlapCm ?? pricing.defaultSlidingOverlapCm ?? 4

  const carcassItems = calculateCarcassM2({
    widthCm: params.width,
    heightCm: params.height,
    depthCm: params.depth,
    columns: safeColumns,
    plinthHeightCm: plintHeight,
    plinthIsSeparatePiece,
    hasBackPanel: params.hasBackPanel ?? true,
  })

  const facadeItems = calculateFacadeM2({
    type: 'wardrobe',
    widthCm: params.width,
    heightCm: params.height,
    plinthHeightCm: plintHeight,
    plinthIsSeparatePiece,
    doors: safeDoors,
    doorType,
    slidingOverlapCm,
  })

  const materialCost = calculateMaterialCost(carcassItems, facadeItems, pricing)

  const hardwareBase = pricing.hardwareBase
  const perDoor = pricing.hardwarePerDoor ?? 0
  const slidingExtra =
    doorType === 'sliding' ? (pricing.slidingSystemExtra ?? 0) : 0

  const hardwareCost = hardwareBase + safeDoors * perDoor + slidingExtra

  const totalBeforeVat =
    materialCost +
    hardwareCost +
    params.sectionsPrice +
    params.templatesExtraCost

  return round10(Math.round(totalBeforeVat * pricing.vatMultiplier * PRICE_DISCOUNT))
}

// ============================================================================
// RACK PRICE CALCULATION (m²)
// ============================================================================

export interface RackPriceParams {
  width: number
  height: number
  depth: number
  columns: number
  templatesExtraCost: number

  hasBackPanel?: boolean
  plintHeight?: number
  plinthIsSeparatePiece?: boolean
}

export function calculateRackPrice(params: RackPriceParams): number {
  const { pricing, dimensions } = getConstraints('rack')

  const safeColumns = Math.max(1, Math.floor(params.columns))
  const plintHeight = params.plintHeight ?? dimensions.plintHeight.default
  const plinthIsSeparatePiece = params.plinthIsSeparatePiece ?? true

  const carcassItems = calculateCarcassM2({
    widthCm: params.width,
    heightCm: params.height,
    depthCm: params.depth,
    columns: safeColumns,
    plinthHeightCm: plintHeight,
    plinthIsSeparatePiece,
    hasBackPanel: params.hasBackPanel ?? true,
  })

  const facadeItems = calculateFacadeM2({
    type: 'rack',
    widthCm: params.width,
    heightCm: params.height,
    plinthHeightCm: plintHeight,
    plinthIsSeparatePiece,
  })

  const materialCost = calculateMaterialCost(carcassItems, facadeItems, pricing)
  const hardwareCost = pricing.hardwareBase

  const totalBeforeVat = materialCost + hardwareCost + params.templatesExtraCost
  return round10(Math.round(totalBeforeVat * pricing.vatMultiplier * PRICE_DISCOUNT))
}

// ============================================================================
// GENERIC PRICE CALCULATION (stand / bedside / tv-stand) (m²)
// ============================================================================

export function calculatePrice(
  type: FurnitureType,
  dimensions: {
    width: number
    height: number
    depth: number
    plintHeight?: number
  },
  columns: number,
  drawers: number = 3,
  columnConfigTypes?: ColumnConfigurationType[],
  isPushOpening?: boolean
): number {
  if (type === 'wardrobe') {
    throw new Error('Use calculateWardrobePrice() for wardrobes.')
  }
  if (type === 'rack') {
    throw new Error('Use calculateRackPrice() for racks.')
  }

  const { pricing, dimensions: dims } = getConstraints(type)

  const safeColumns = Math.max(1, Math.floor(columns))
  const plintHeight = dimensions.plintHeight ?? dims.plintHeight.default
  const plinthIsSeparatePiece = true

  // 1) Material m²
  const carcassItems = calculateCarcassM2({
    widthCm: dimensions.width,
    heightCm: dimensions.height,
    depthCm: dimensions.depth,
    columns: safeColumns,
    plinthHeightCm: plintHeight,
    plinthIsSeparatePiece,
    hasBackPanel: true,
  })

  const facadeItems = calculateFacadeM2({
    type,
    widthCm: dimensions.width,
    heightCm: dimensions.height,
    plinthHeightCm: plintHeight,
    plinthIsSeparatePiece,
  })

  const materialCost = calculateMaterialCost(carcassItems, facadeItems, pricing)

  // 2) Drawer count (affects hardware/labor)
  let totalDrawerCount: number
  if (columnConfigTypes && columnConfigTypes.length > 0) {
    totalDrawerCount = columnConfigTypes
      .slice(0, safeColumns)
      .reduce((sum, cfgType) => sum + getDrawerCount(cfgType), 0)
  } else {
    totalDrawerCount = Math.max(0, drawers) * safeColumns
  }

  const perDrawer = pricing.hardwarePerDrawer ?? 0
  const pushExtra = isPushOpening ? (pricing.pushToOpenExtraPerDrawer ?? 0) : 0

  const hardwareCost =
    pricing.hardwareBase + totalDrawerCount * (perDrawer + pushExtra)

  const totalBeforeVat = materialCost + hardwareCost + 1500
  return round10(Math.round(totalBeforeVat * pricing.vatMultiplier * PRICE_DISCOUNT))
}

// ============================================================================
// CONSTRAINT REGISTRY
// ============================================================================

const FURNITURE_CONSTRAINTS = {
  stand: STAND_CONSTRAINTS,
  bedside: BEDSIDE_CONSTRAINTS,
  'tv-stand': TV_STAND_CONSTRAINTS,
  wardrobe: WARDROBE_CONSTRAINTS,
  'wardrobe-new-collection': WARDROBE_NEW_COLLECTION_CONSTRAINTS,
  rack: RACK_CONSTRAINTS,
} as const

export type FurnitureType = keyof typeof FURNITURE_CONSTRAINTS

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getConstraints(type: FurnitureType): ProductConstraints {
  return FURNITURE_CONSTRAINTS[type]
}